import React, { useEffect, useMemo, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box, Text, VStack, HStack, Stack, Button, Avatar, SimpleGrid, Select,
  Divider, useColorModeValue, Icon, useDisclosure, Drawer, DrawerOverlay,
  DrawerContent, DrawerHeader, DrawerBody, DrawerCloseButton, Tag, TagLabel, Flex,
  Wrap, WrapItem, 
  RangeSlider, RangeSliderTrack, RangeSliderFilledTrack, RangeSliderThumb,
} from "@chakra-ui/react";

import {
  getExpertMetricCatalog,
  getMetricLevelInsights,
  getExpertImpacts
} from "../../actions/sector/sectorActions";
import { getExpertDetailProfile } from "../../actions/user/userActions";
import { getClientDetails } from "../../actions/client/clientActions";

/* ---------- Icon (kept) ---------- */
function CalendarIcon(props) {
  return (
    <Icon viewBox="0 0 24 24" boxSize="1em" {...props}>
      <path
        fill="currentColor"
        d="M7 2a1 1 0 0 1 1 1v1h8V3a1 1 0 1 1 2 0v1h1a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h1V3a1 1 0 1 1 2 0v1Zm13 7H4v10a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V9ZM5 7h14V6a1 1 0 0 0-1-1h-1v1a1 1 0 1 1-2 0V5H8v1a1 1 0 1 1-2 0V5H5a1 1 0 0 0-1 1v1Z"
      />
    </Icon>
  );
}

/* ---------- Shared styles ---------- */
const leftCard = {
  bg: "white",
  borderRadius: "14px",
  boxShadow: "0 2px 10px rgba(16,24,40,0.06)",
  border: "1px solid",
  borderColor: "gray.100",
};

/* ---------- Expert Card (unchanged UI) ---------- */
const ExpertCard = ({ name, image, scopes = [], onClick, isLoading, assignmentFee ,isExpanded}) => {
  const cardBg = useColorModeValue("white", "gray.800");

  return (
    <Box
      bg={cardBg}
      borderRadius="16px"
      border="1px solid"
      borderColor="gray.100"
      boxShadow="0 2px 12px rgba(16,24,40,0.06)"
       p={isExpanded ? 8 : 6}
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => (e.key === "Enter" ? onClick?.() : null)}
      _hover={{ boxShadow: "0 4px 16px rgba(16,24,40,0.10)" }}
      cursor="pointer"
      // opacity={isLoading ? 0.6 : 1}
      position="relative"
    >
      <HStack spacing={4} align="flex-start">
        <Avatar name={name} src={image} size="lg" />
        <VStack align="start" spacing={2} flex={1}>
          <Text fontWeight="700" fontSize="lg">{name} {isLoading}</Text>
          {assignmentFee && (
            <Text fontSize="sm" color="gray.600" fontWeight="600">
              Assignment Fee: {assignmentFee}
            </Text>
          )}
          <Box w="100%">
            <Text fontSize="xs" color="gray.500" mb={1}>Scopes Delivered</Text>
            <HStack spacing={2} wrap="wrap">
              {scopes?.length
                ? scopes.slice(0, 3).map((s, i) => (
                    <Tag key={i} size="sm" variant="subtle" colorScheme="teal">
                      <TagLabel>{s}</TagLabel>
                    </Tag>
                  ))
                : <Text fontSize="sm" color="gray.500">—</Text>}
            </HStack>
          </Box>
        </VStack>
      </HStack>
    </Box>
  );
};

/* ---------- Main Component ---------- */
function FindExperts() {
  const dispatch = useDispatch();

  /* === clientReducer (your shape) === */
  const { loading: clientLoading, data: clientData } = useSelector(
    (state) => state.clientReducer
  );

  // Fallbacks if clientReducer isn't hydrated yet
  const queryParams = new URLSearchParams(window.location.search || "");
  
  const urlCompanyId = queryParams.get("companyId") || queryParams.get("id") || null;
  const lsCompanyId =
    (typeof window !== "undefined" &&
      (localStorage.getItem("companyId") || localStorage.getItem("activeCompanyId"))) ||
    null;

  const companyId = clientData?._id || urlCompanyId || lsCompanyId || null;
  const year = String(new Date().getFullYear());

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedExpert, setSelectedExpert] = useState(null);

  // Filters
  const [selectedMetricKey, setSelectedMetricKey] = useState("");
  const [selectedRoles, setSelectedRoles] = useState([]); // multi-select

  // Store canonical range **in Lakhs**; display converts to Thousands when needed
  const [feeRangeLakh, setFeeRangeLakh] = useState([1, 10]); // 1–10 Lakh

  const lastRecKey = useRef(null);
  const [loadingImpacts, setLoadingImpacts] = useState({});
  const [fetchedExperts, setFetchedExperts] = useState(new Set());

  // Experts (UI source)
  const { data: rawData } = useSelector(
    (state) => state.expertDetailProfileReducer || {}
  );

  // Metric catalog / insights
  const { data: metricData, loading: metricLoading } = useSelector(
    (s) => s.metricExpertReducer || {}
  );
  const { data: insightsData } = useSelector(
    (s) => s.metricLevelInsightsReducer || {}
  );
  const recommendations = insightsData?.recommendations || [];

  /* ⬇️ impacts selector */
  const impactsRoot = useSelector((s) => {
    return s.expertImpactReducer || s.expertImpactsReducer || s.impacts || {};
  });

  /* 0) Hydrate client if missing but id is available */
  useEffect(() => {
    if (!clientData && (urlCompanyId || lsCompanyId)) {
      const id = urlCompanyId || lsCompanyId;
      if (!id) return;
      if (window.__clientFetchId__ !== id) {
        window.__clientFetchId__ = id;
        dispatch(getClientDetails(id));
      }
    }
  }, [clientData, urlCompanyId, lsCompanyId, dispatch]);

  /* 1) Initial loads */
  useEffect(() => {
    dispatch(getExpertDetailProfile());
    dispatch(getExpertMetricCatalog());
  }, [dispatch]);

  /* 2) Recommendations */
  useEffect(() => {
    if (!companyId) return;
    const key = `${companyId}_${year}`;
    if (lastRecKey.current === key) return;
    lastRecKey.current = key;
    dispatch(getMetricLevelInsights({ companyId, year }));
  }, [dispatch, companyId, year]);

  /* 3) Metric options (formula display) */
  const metrics = useMemo(() => {
    const payload =
      Array.isArray(metricData?.data?.items)
        ? metricData.data.items
        : Array.isArray(metricData?.data)
        ? metricData.data
        : Array.isArray(metricData)
        ? metricData
        : [];
    const toSum = (arr) => (Array.isArray(arr) ? arr.join(" + ") : "");
    return payload
      .map((m) => {
        const num = toSum(m?.numeratorComponents);
        const den = toSum(m?.denominatorComponents);
        const formula = [num, den].every(Boolean) ? `${num} / ${den}` : m?.metricKey;
        return { metricKey: m.metricKey, label: m.label, formula, raw: m };
      })
      .filter((x) => x.metricKey);
  }, [metricData]);

  /* 4) Experts array */
  const expertsFromApi = useMemo(() => {
    const payload = rawData?.data ?? rawData ?? [];
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.items)) return payload.items;
    return payload ? [payload] : [];
  }, [rawData]);

  /* 4.1) OPTIMIZED impacts getter */
  const getExpertImpactsData = (expertId) => {
    if (!expertId) return { items: [], loaded: false, fetched: false };
    
    const hasBeenFetched = fetchedExperts.has(expertId);
    
    const findItemsInObject = (obj) => {
      if (!obj || typeof obj !== 'object') return null;
      if (Array.isArray(obj.items)) {
        const filteredItems = obj.items.filter(item => item.expertId === expertId);
        if (filteredItems.length > 0) return filteredItems;
      }
      for (let key in obj) {
        if (obj[key] && typeof obj[key] === 'object') {
          const result = findItemsInObject(obj[key]);
          if (result && result.length > 0) return result;
        }
      }
      return null;
    };

    const foundItems = findItemsInObject(impactsRoot);
    if (foundItems && foundItems.length > 0) {
      return { items: foundItems, loaded: true, fetched: true };
    }

    if (hasBeenFetched) {
      return { items: [], loaded: true, fetched: true };
    }

    return { items: [], loaded: false, fetched: false };
  };

  /* 5) Selected metric & component set */
  const selectedMetricObj = useMemo(
    () => metrics.find((m) => m.metricKey === selectedMetricKey),
    [metrics, selectedMetricKey]
  );

  const componentSet = useMemo(() => {
    if (!selectedMetricObj) return new Set();
    return new Set([
      ...(selectedMetricObj?.raw?.numeratorComponents || []),
      ...(selectedMetricObj?.raw?.denominatorComponents || []),
    ]);
  }, [selectedMetricObj]);

  /* 6) requiredPairs */
  const requiredPairs = useMemo(() => {
    if (!selectedMetricKey) return null;

    const rec = Array.isArray(recommendations)
      ? recommendations.find((r) => r.metricKey === selectedMetricKey)
      : null;

    if (rec?.actions?.length) {
      return rec.actions.map((a) => ({
        componentKey: a.componentKey,
        direction: a.action,
      }));
    }

    const catalogMetric = metrics.find((m) => m.metricKey === selectedMetricKey)?.raw;
    if (catalogMetric?.componentRules?.length) {
      return catalogMetric.componentRules.map((cr) => ({
        componentKey: cr.componentKey,
        direction: cr.desiredDirectionIfUnfavorable,
      }));
    }

    return null;
  }, [selectedMetricKey, recommendations, metrics]);

  /* Role-pre-filtered experts BEFORE fetching impacts */
  const expertsRoleFiltered = useMemo(() => {
    if (selectedRoles.length === 0) return expertsFromApi;
    const set = new Set(selectedRoles.map((r) => r.toLowerCase()));
    return expertsFromApi.filter(
      (ex) => set.has((ex?.expert_title || "").toLowerCase())
    );
  }, [expertsFromApi, selectedRoles]);

  /* 7) Fetch impacts ONLY for role-filtered candidates when metric selected */
  useEffect(() => {
    if (!selectedMetricKey) return;
    if (!expertsRoleFiltered.length) return;

    const toFetch = expertsRoleFiltered
      .map((e) => e?._id)
      .filter((id) => {
        if (!id) return false;
        const snap = getExpertImpactsData(id);
        return !snap.fetched;
      });

    if (toFetch.length > 0) {
      toFetch.forEach((id) => {
        setLoadingImpacts(prev => ({ ...prev, [id]: true }));
        setFetchedExperts(prev => new Set([...prev, id]));
        dispatch(getExpertImpacts(id));
      });
    }
  }, [selectedMetricKey, expertsRoleFiltered, dispatch]);

  // Helper function to extract assignment fee from expert data
  const getAssignmentFee = (expert) => {
    return expert?.["Assignment fee from"] || 
           expert?.assignment_fee || 
           expert?.assignmentFee || 
           null;
  };

  // Helper function to convert assignment fee to numeric value in lakhs for filtering
  const getAssignmentFeeValue = (feeString) => {
    if (!feeString) return 0;
    const numericMatch = feeString.toString().match(/(\d+[,.]?\d*)/);
    if (numericMatch) {
      let value = parseFloat(numericMatch[1].replace(/,/g, ''));
      return value; // already in Lakhs in your data
    }
    return 0;
  };

  /* 8) FINAL filtering logic using real API data */
  const expertsFiltered = useMemo(() => {
    let filtered = expertsFromApi;

    // role filter first
    if (selectedRoles.length > 0) {
      const set = new Set(selectedRoles.map((r) => r.toLowerCase()));
      filtered = filtered.filter(
        (ex) => set.has((ex?.expert_title || "").toLowerCase())
      );
    }

    // assignment fee filter in lakhs (canonical)
    const [minFee, maxFee] = Array.isArray(feeRangeLakh) ? feeRangeLakh : [1, 10];
    const feeFiltered = filtered.filter((ex) => {
      const fee = getAssignmentFee(ex);
      if (!fee) return true;
      const feeValue = getAssignmentFeeValue(fee); // in Lakhs
      return feeValue >= minFee && feeValue <= maxFee;
    });

    filtered = feeFiltered;

    // then metric filter - ONLY if metric is selected
    if (selectedMetricKey && selectedMetricObj) {
      filtered = filtered.filter((ex) => {
        const { items, loaded, fetched } = getExpertImpactsData(ex._id);
        if (!fetched) return true;
        if (loaded && items.length === 0) return false;

        if (requiredPairs?.length) {
          const hasMatch = requiredPairs.some((req) =>
            items.some(
              (imp) =>
                req.componentKey === imp.componentKey &&
                req.direction === imp.direction
            )
          );
          return hasMatch;
        }
        return items.some((imp) => componentSet.has(imp.componentKey));
      });
    }

    return filtered;
  }, [
    expertsFromApi,
    selectedRoles,
    selectedMetricKey,
    selectedMetricObj,
    requiredPairs,
    componentSet,
    impactsRoot,
    fetchedExperts,
    feeRangeLakh,
  ]);

  /* 9) Get match status for UI */
  const getExpertMatchStatus = (expertId) => {
    if (!selectedMetricKey || !requiredPairs) return false;
    const { items, loaded, fetched } = getExpertImpactsData(expertId);
    if (!fetched || !loaded || items.length === 0) return false;

    return requiredPairs.some((req) =>
      items.some(
        (imp) =>
          req.componentKey === imp.componentKey &&
          req.direction === imp.direction
      )
    );
  };

  /* 10) Check loading states */
  const getExpertLoadingState = (expertId) => {
    const impacts = getExpertImpactsData(expertId);
    return loadingImpacts[expertId] || !impacts.fetched;
  };

  /* 11) Role options */
  const baseForRoleOptions = selectedMetricObj ? expertsFiltered : expertsFromApi;
  const roleOptions = useMemo(() => {
    const titles = (baseForRoleOptions || [])
      .map((e) => e?.expert_title)
      .filter(Boolean);
    return Array.from(new Set(titles)).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" })
    );
  }, [baseForRoleOptions]);

  /* === UI helpers & handlers === */
  const pageBg = useColorModeValue("gray.50", "gray.900");
  const getScopes = (ex) =>
    Array.isArray(ex?.["Scopes Delivered"])
      ? ex["Scopes Delivered"].map((s) => s?.scope).filter(Boolean)
      : [];
  const toggleRole = (role) => {
    setSelectedRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
    );
  };
  const clearRoles = () => setSelectedRoles([]);
  const [feeUnit, setFeeUnit] = useState("Lakh"); 

  // Derived values for the slider UI
  const displayRange = feeUnit === "Lakh"
    ? feeRangeLakh
    : [Math.round(feeRangeLakh[0] * 100), Math.round(feeRangeLakh[1] * 100)]; 

  const displayMin = feeUnit === "Lakh" ? 1 : 1;         
  const displayMax = feeUnit === "Lakh" ? 10 : 100;      
  const displayStep = feeUnit === "Lakh" ? 0.5 : 5;     

  return (
    <Box p={{ base: 4, md: 6 }} bg={pageBg}>
      <Stack direction={{ base: "column", lg: "row" }} align="flex-start" spacing={6}>
        {/* Left Panel */}
        <Box flexBasis={{ base: "100%", lg: "28%" }} w={{ base: "100%", lg: "28%" }} p={5} sx={leftCard}>
          <Text fontSize="sm" color="gray.500" mb={4}>Find by</Text>

          {/* ROLE / FUNCTION SERVED  */}
          <Box mb={6}>
            <Text fontWeight="700" fontSize="sm" mb={3} color="gray.700">
              ROLE / FUNCTION SERVED
            </Text>

            <Flex justify="space-between" align="center" mb={2}>
              <Text fontSize="xs" color="gray.500">
                {selectedRoles.length ? `${selectedRoles.length} selected` : "No role selected"}
              </Text>
              <Button
                size="xs"
                variant="ghost"
                colorScheme="teal"
                onClick={clearRoles}
                isDisabled={selectedRoles.length === 0}
              >
                Clear all
              </Button>
            </Flex>

            <Wrap spacing={2}>
              {roleOptions.length === 0 ? (
                <Text color="gray.500">No roles available</Text>
              ) : (
                roleOptions.map((r) => {
                  const selected = selectedRoles.includes(r);
                  return (
                    <WrapItem key={r}>
                      <Button
                        onClick={() => toggleRole(r)}
                        size="sm"
                        borderRadius="10px"
                        variant={selected ? "solid" : "outline"}
                        colorScheme="teal"
                        bg={selected ? "#30D5C8" : "transparent"}
                        color={selected ? "white" : "teal.600"}
                        borderColor="#30D5C8"
                        _hover={{ bg: selected ? "#26bfb3" : "teal.50" }}
                        _active={{ bg: selected ? "#20a79d" : "teal.100" }}
                      >
                        {r}
                      </Button>
                    </WrapItem>
                  );
                })
              )}
            </Wrap>
          </Box>

          {/* METRIC IMPROVED */}
          <Box mb={6}>
            <Text fontWeight="700" fontSize="sm" mb={3} color="gray.700">
              METRIC IMPROVED
            </Text>
            <Select
              value={selectedMetricKey || ""}
              onChange={(e) => setSelectedMetricKey(e.target.value)}
              variant="filled"
              bg="#30D5C8"
              borderColor="#30D5C8"
              _focus={{ borderColor: "#30D5C8", boxShadow: "none" }}
              _hover={{ bg: "#30D5C8" }}
              iconColor="gray.400"
              isDisabled={metricLoading || metrics.length === 0}
              placeholder={metricLoading ? "Loading..." : "Select a metric"}
            >
              {metrics.map((m) => (
                <option key={m.metricKey} value={m.metricKey}>
                  {m.label ? `${m.label}: ${m.formula}` : m.formula}
                </option>
              ))}
            </Select>
            {selectedMetricKey && (
              <Text fontSize="xs" color="gray.600" mt={2}>
                Filtering experts who can improve this metric
              </Text>
            )}
          </Box>

          {/* APPROXIMATE ASSIGNMENT FEE — Range Slider with unit chips */}
          <Box mb={6}>
            <Text fontWeight="700" fontSize="sm" mb={3} color="gray.700">
              APPROXIMATE ASSIGNMENT FEE
            </Text>

            {/* Unit chips */}
            <HStack spacing={2} mb={3}>
              <Button
                size="sm"
                borderRadius="full"
                variant={feeUnit === "Thousands" ? "solid" : "outline"}
                colorScheme="teal"
                bg={feeUnit === "Thousands" ? "#30D5C8" : "transparent"}
                color={feeUnit === "Thousands" ? "white" : "teal.600"}
                borderColor="#30D5C8"
                _hover={{ bg: feeUnit === "Thousands" ? "#26bfb3" : "teal.50" }}
                onClick={() => setFeeUnit("Thousands")}
              >
                Thousands
              </Button>
              <Button
                size="sm"
                borderRadius="full"
                variant={feeUnit === "Lakh" ? "solid" : "outline"}
                colorScheme="teal"
                bg={feeUnit === "Lakh" ? "#30D5C8" : "transparent"}
                color={feeUnit === "Lakh" ? "white" : "teal.600"}
                borderColor="#30D5C8"
                _hover={{ bg: feeUnit === "Lakh" ? "#26bfb3" : "teal.50" }}
                onClick={() => setFeeUnit("Lakh")}
              >
                Lacs
              </Button>
            </HStack>

            <VStack spacing={3} align="stretch">
              <HStack justify="space-between">
                <Text fontSize="sm" color="gray.600" fontWeight="600">
                  {feeUnit === "Lakh" ? "1 Lakh" : "1 Thousand"}
                </Text>
                <Text fontSize="sm" color="gray.600" fontWeight="600">
                  {feeUnit === "Lakh" ? "10 Lakh" : "100 Thousand"}
                </Text>
              </HStack>

              <Box position="relative">
                <RangeSlider
                  aria-label={["min-fee", "max-fee"]}
                  value={displayRange}
                  min={displayMin}
                  max={displayMax}
                  step={displayStep}
                  colorScheme="teal"
                  onChange={(val) => {
                    if (!Array.isArray(val)) return;
                    if (feeUnit === "Lakh") {
                      setFeeRangeLakh(val);
                    } else {
                      const toLakhs = [val[0] / 100, val[1] / 100];
                      setFeeRangeLakh([
                        Math.max(1, Math.min(10, Number(toLakhs[0].toFixed(2)))),
                        Math.max(1, Math.min(10, Number(toLakhs[1].toFixed(2)))),
                      ]);
                    }
                  }}
                >
                  <RangeSliderTrack bg="gray.200">
                    <RangeSliderFilledTrack bg="#30D5C8" />
                  </RangeSliderTrack>
                  <RangeSliderThumb boxSize={6} index={0} bg="#30D5C8" />
                  <RangeSliderThumb boxSize={6} index={1} bg="#30D5C8" />
                </RangeSlider>

                {/* Side pointers at start and end (decorative) */}
                <Text position="absolute" top="-18px" left="0" fontSize="xs">▼</Text>
                <Text position="absolute" top="-18px" right="0" fontSize="xs">▼</Text>
              </Box>

              <HStack justify="space-between" mt={2}>
                <Text fontSize="sm" fontWeight="600">
                  {feeUnit === "Lakh"
                    ? `${displayRange[0]} Lakh`
                    : `${displayRange[0]} Thousand`}
                </Text>
                <Text fontSize="sm" fontWeight="600">
                  {feeUnit === "Lakh"
                    ? `${displayRange[1]} Lakh`
                    : `${displayRange[1]} Thousand`}
                </Text>
              </HStack>
            </VStack>
          </Box>

        </Box>

        {/* Right Panel — Expert Cards */}
        <Box flex="1">
          <Text fontWeight="700" fontSize="sm" color="gray.700" mb={3}>
            RECOMMENDED EXPERTS {selectedMetricKey && `for ${selectedMetricKey}`}
          </Text>

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            {expertsFiltered.length > 0 ? (
              expertsFiltered.map((ex) => {
                const isLoading = getExpertLoadingState(ex._id);
                const assignmentFee = getAssignmentFee(ex);
                
                return (
                  <ExpertCard
                    key={ex._id}
                    name={ex.expert_name}
                    image={ex.profile_image}
                    scopes={getScopes(ex)}
                    isLoading={isLoading}
                    assignmentFee={assignmentFee}
                    onClick={() => { setSelectedExpert(ex); onOpen(); }}
                  />
                );
              })
            ) : (
              <Text color="gray.500" fontSize="sm">
                {selectedMetricKey 
                  ? "No experts found with experience improving this metric." 
                  : "No experts found for selected filters."}
              </Text>
            )}
          </SimpleGrid>
        </Box>
      </Stack>

      {/* Drawer for Details (unchanged) */}
      <Drawer placement="right" onClose={onClose} isOpen={isOpen}>
        <DrawerOverlay />
        <DrawerContent
          position="fixed"
    mt={{ base: "70px", md: "100px", lg: "140px" }}  
    mr={{ base: "12px", md: "32px", lg: "25px" }} 
    bottom={{ base: "10px", sm: "20px" }} 
    w={{ base: "90%", sm: "400px", md: "420px", lg: "250px" }} 
    maxW="400px"
    border="1px solid"
    borderColor="gray.100"
    boxShadow="0 8px 24px rgba(0,0,0,0.12)"
    bg="white"
    overflowY="auto"
        
        >
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px">
            {selectedExpert?.expert_name || "Expert"}
          </DrawerHeader>
          <DrawerBody>
            <HStack align="flex-start" spacing={4} mb={4}>
              <Avatar name={selectedExpert?.expert_name} src={selectedExpert?.profile_image} size="xl" />
              <VStack align="start" spacing={1}>
                <Text fontWeight="700">{selectedExpert?.expert_title}</Text>
                {(() => {
                  const langText =
                    Array.isArray(selectedExpert?.languages)
                      ? selectedExpert.languages.join(", ")
                      : selectedExpert?.["Language Fluency"];
                  return langText ? (
                    <Tag size="sm" colorScheme="gray" variant="subtle">
                      <TagLabel>{langText}</TagLabel>
                    </Tag>
                  ) : null;
                })()}
              </VStack>
            </HStack>

            <HStack spacing={3} mb={4}>
              <Box flex={1} bg="gray.50" borderRadius="10px" p={3}>
                <Text fontSize="xs" color="gray.500">Time taken</Text>
                <Text fontWeight="600">{selectedExpert?.["Time Taken"] || "—"}</Text>
              </Box>
              <Box flex={1} bg="gray.50" borderRadius="10px" p={3}>
                <Text fontSize="xs" color="gray.500">Assignment fee from</Text>
                <Text fontWeight="600">{selectedExpert?.["Assignment fee from"] || "—"}</Text>
              </Box>
            </HStack>

            <Box mt={2}>
              <Text fontWeight="700" mb={2}>Scopes Delivered</Text>
              <VStack align="stretch" spacing={1}>
                {(Array.isArray(selectedExpert?.["Scopes Delivered"]) ? selectedExpert["Scopes Delivered"] : []).map((s, idx) => (
                  <Text key={idx} fontSize="sm">• {s?.scope}</Text>
                ))}
              </VStack>
            </Box>

            <Button mt={6} w="100%" colorScheme="teal">Book Discovery Call</Button>
          </DrawerBody>
        </DrawerContent>
      </Drawer>



    </Box>
  );
}

export default FindExperts;
